import { useCallback, useRef } from "react";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { Supplier } from "../models/Supplier";
import { useTableData } from "../hooks/useTableData";

interface SuppliersTableProps {
	suppliers: Supplier[];
	selectedSupplier: Supplier | null;
	onRowSelected: (supplier: Supplier | null) => void;
	onDelete: (supplier: Supplier) => void;
}

export const SuppliersTable = ({
	suppliers,
	selectedSupplier,
	onRowSelected,
	onDelete,
}: SuppliersTableProps) => {
	const gridRef = useRef<AgGridReact<Supplier>>(null);
	const { columnDefs } = useTableData({ onDelete });

	const onGridReady = useCallback((_params: GridReadyEvent<Supplier>) => {
		// Let flex columns handle sizing naturally
	}, []);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<Supplier>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const getRowId = useCallback((params: { data: Supplier }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<AgGridReact<Supplier>
			ref={gridRef}
			rowData={suppliers}
			columnDefs={columnDefs}
			defaultColDef={globalDefaultColDef}
			onGridReady={onGridReady}
			onRowClicked={onRowClicked}
			getRowId={getRowId}
			rowSelection="single"
			suppressRowClickSelection={false}
			suppressContextMenu
			alwaysShowVerticalScroll
			alwaysShowHorizontalScroll
			suppressColumnVirtualisation
			animateRows={false}
			rowHeight={36}
			headerHeight={36}
			getRowStyle={(params) => {
				if (selectedSupplier?.Id === params.data?.Id) {
					return { backgroundColor: "#e3f2fd", color: "inherit" };
				}
				if (params.data && !params.data.Active) {
					return { backgroundColor: "#f5f5f5", color: "#9e9e9e" };
				}
				return undefined;
			}}
		/>
	);
};
